import { Typography } from '@smartgift/frontend-toolkit';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';

import { useDebounceEffect } from '@hero/hooks';

import { UPLOAD_LOGO_SCREEN } from './constants';
import { UploadLogoContext } from './context';
import * as S from './style';

const TO_RADIANS = Math.PI / 180;

function canvasPreview(image, canvas, crop, scale = 1, rotate = 0) {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  // devicePixelRatio slightly increases sharpness on retina devices
  // at the expense of slightly slower render times and needing to
  // size the image back down if you want to download/upload and be
  // true to the images natural size.
  const pixelRatio = window.devicePixelRatio;
  // const pixelRatio = 1

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // 5) Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // 4) Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // 3) Rotate around the origin
  ctx.rotate(rotateRads);
  // 2) Scale the image
  ctx.scale(scale, scale);
  // 1) Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );

  ctx.restore();
}

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

function CropImage({ aspect = false, scale = 1, rotate = 0, isLoading }) {
  const {
    setIsOpen,
    completedCrop,
    setCompletedCrop,
    cropImage,
    setModalProps,
    setCurrentScreen,
    setPreviewImage,
    crop,
    setCrop
  } = useContext(UploadLogoContext);

  const { t } = useTranslation();
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);

  function onImageLoad(e) {
    if (!crop) {
      const { width, height } = e.currentTarget;

      if (aspect) {
        setCrop(centerAspectCrop(width, height, aspect));
      } else {
        setCrop({
          unit: '%',
          width: 100,
          height: 100
        });
      }
    }
  }

  useEffect(() => {
    const primaryButtonOnClick = () => {
      const b64Image = previewCanvasRef.current.toDataURL('image/png');
      setPreviewImage(b64Image);
      setCurrentScreen(UPLOAD_LOGO_SCREEN.preview);
    };
    const secondaryButtonOnClick = () => {
      const primaryButtonOnClick = () => setIsOpen(false);
      setCrop();
      setCompletedCrop();
      setCurrentScreen(UPLOAD_LOGO_SCREEN.upload);
      setModalProps({
        primaryButtonProps: {
          children: '',
          onClick: primaryButtonOnClick
        },
        secondaryButtonProps: {}
      });
    };
    setModalProps(props => ({
      ...props,
      primaryButtonProps: {
        loading: isLoading,
        children: t('confirmAndUpload'),
        endIcon: 'next',
        onClick: primaryButtonOnClick
      },
      secondaryButtonProps: {
        ...props.secondaryButtonProps,
        children: t('startOver'),
        onClick: secondaryButtonOnClick
      }
    }));
  }, [isLoading]);

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);
      }
    },
    100,
    [completedCrop, imgRef, previewCanvasRef, scale, rotate]
  );

  return (
    <S.CropImageArea>
      <Typography style={{ maxWidth: 400, textAlign: 'center' }} variant="body1Bold">
        {t('cropLogoHelpText')}
      </Typography>
      <ReactCrop
        crop={crop}
        onChange={(_, percentCrop) => {
          setCrop(percentCrop);
        }}
        onComplete={c => {
          setCompletedCrop(c);
        }}
        aspect={aspect}
        minWidth={200}
        minHeight={100}
      >
        <img
          ref={imgRef}
          alt="Crop me"
          src={cropImage}
          style={{
            maxWidth: 700,
            maxHeight: 500,
            transform: `scale(${scale}) rotate(${rotate}deg)`
          }}
          onLoad={onImageLoad}
        />
      </ReactCrop>
      {completedCrop && <canvas ref={previewCanvasRef} style={{ display: 'none' }} />}
    </S.CropImageArea>
  );
}

export { CropImage };
